import { jwtDecode } from 'jwt-decode';
import type { PublicWebinarMe } from '@/features/webinar/types';

export type PublicLiveJwtKind = 'INVITE' | 'SUBSCRIPTION';

type RawPayload = {
  type?: string;
  attendeeId?: number | string;
  email?: string;
  firstName?: string;
  webinarId?: number | string;
  workspaceId?: number | string;
  sessionId?: number | string;
};

function attendeeIdFromPayload(d: RawPayload): number | null {
  const v = d.attendeeId;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

export function getPublicLiveJwtKind(token: string | null): PublicLiveJwtKind | null {
  if (!token) return null;
  try {
    const d = jwtDecode<RawPayload>(token);
    const t = typeof d.type === 'string' ? d.type.trim().toUpperCase() : '';
    if (t === 'INVITE') return 'INVITE';
    return 'SUBSCRIPTION';
  } catch {
    return 'SUBSCRIPTION';
  }
}

export function subscriptionJwtHasFullAttendeeClaims(token: string): boolean {
  try {
    const d = jwtDecode<RawPayload>(token);
    if (typeof d.type === 'string' && d.type.trim().toUpperCase() === 'INVITE') {
      return false;
    }
    const id = attendeeIdFromPayload(d);
    const em = d.email;
    const fn = d.firstName;
    return (
      id != null &&
      typeof em === 'string' &&
      em.trim() !== '' &&
      typeof fn === 'string' &&
      fn.trim() !== ''
    );
  } catch {
    return false;
  }
}

export function parsePublicWebinarMeFromSubscriptionToken(
  token: string,
  fallbackWebinarId: number,
): PublicWebinarMe | null {
  try {
    const d = jwtDecode<RawPayload>(token);
    const id = attendeeIdFromPayload(d);
    if (id == null) return null;
    const em = d.email;
    const fn = d.firstName;
    if (typeof em !== 'string' || em.trim() === '') return null;
    if (typeof fn !== 'string' || fn.trim() === '') return null;

    const widRaw = d.workspaceId;
    const sidRaw = d.sessionId;
    const wid =
      typeof widRaw === 'number' ? widRaw : typeof widRaw === 'string' ? Number(widRaw) : 0;
    const sid =
      typeof sidRaw === 'number' ? sidRaw : typeof sidRaw === 'string' ? Number(sidRaw) : 0;

    const wbnRaw = d.webinarId;
    let webinarId = fallbackWebinarId;
    if (typeof wbnRaw === 'number' && !Number.isNaN(wbnRaw)) {
      webinarId = wbnRaw;
    } else if (typeof wbnRaw === 'string' && wbnRaw.trim() !== '') {
      const n = Number(wbnRaw);
      if (!Number.isNaN(n)) webinarId = n;
    }

    const trimmedFn = fn.trim();
    return {
      id,
      webinarId,
      workspaceId: Number.isNaN(wid) ? 0 : wid,
      sessionId: Number.isNaN(sid) ? 0 : sid,
      fullName: trimmedFn,
      firstName: trimmedFn,
      email: em.trim(),
      status: '',
    };
  } catch {
    return null;
  }
}

export type SubscriptionAttendeeChatIdentity = {
  userId: string;
  userName: string;
  email: string;
};

export function getSubscriptionAttendeeChatIdentity(
  token: string | null,
): SubscriptionAttendeeChatIdentity | null {
  if (!token || !subscriptionJwtHasFullAttendeeClaims(token)) return null;
  const me = parsePublicWebinarMeFromSubscriptionToken(token, 0);
  if (!me) return null;
  return { userId: String(me.id), userName: me.firstName, email: me.email };
}
